import React, { useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Users, Building2, Radio, BadgeCheck, MapPin, Network } from "lucide-react";
import { Reveal } from "../components/motion";
import { imgProps } from "../components/media";
import { PageHero, CtaBand, LegalitaBadge } from "../components/shared";
import { STRUTTURE_STATS, SEDI, CERTS, CLIENTS_ROW1, CLIENTS_ROW2, DOCS, CONTACT, img, gruppo, aurea, logo } from "../data/site";

const HOW = [
  [Users, "Persone formate", "Guardie particolari giurate e personale fiduciario selezionato, con formazione continua in aula e sul campo e certificazione UNI 10459.", "tone-w"],
  [Building2, "Strutture di proprietà", "Oltre 6.000 metri quadrati di strutture nostre, con caveau e un centro dedicato al controllo dei valori. Non dipendiamo da terzi.", ""],
  [Radio, "Due sale operative", "San Cesareo e Bascapè, certificate UNI CEI EN 50518 e sempre collegate: una è il backup dell'altra.", "tone-a"],
  [BadgeCheck, "Procedure certificate", "Qualità, sicurezza, ambiente e responsabilità sociale verificate ogni anno da enti di certificazione indipendenti.", "tone-w"],
  [MapPin, "Presenza nazionale", "Sei aeroporti, nove sedi, due sale conta e servizi di vigilanza non armata in tutta Italia.", ""],
  [Network, "Un gruppo, più competenze", "Con Axentra per la tecnologia e Astra per le investigazioni offriamo una sicurezza completa.", "tone-a"],
];

export default function ChiSiamo() {
  const reduce = useReducedMotion();
  const clienti = [...CLIENTS_ROW1, ...CLIENTS_ROW2];
  /* Se si arriva con l'ancora #gruppo (es. firma "by Gruppo Aurea" del menu), scorri alla sezione */
  useEffect(() => {
    if (window.location.hash !== "#gruppo") return undefined;
    const t = setTimeout(() => {
      const el = document.getElementById("gruppo");
      if (el) el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
    }, 350);
    return () => clearTimeout(t);
  }, [reduce]);
  return (
    <>
      <PageHero
        kick="Chi siamo"
        lines={["Dal 1992, al fianco di", "chi deve essere protetto."]}
        sub="Esperienza nella vigilanza privata dal 1992. Oggi siamo Orion Security, parte del gruppo Aurea Holding 3: strutture di proprietà, due sale operative certificate e presenza in tutta Italia."
        ph={img("pattuglia-colosseo.jpg")}
      />

      <section className="sec-blue sec" data-testid="chi-come-lavoriamo">
        <div className="wrap">
          <Reveal className="sec-head">
            <p className="kick">Chi siamo oggi</p>
            <h2 className="h2">Il nostro modo di lavorare</h2>
            <p style={{ margin: "26px 0 0", color: "var(--muted)" }}>Quello che ci distingue, ogni giorno.</p>
          </Reveal>
          <div className="how-grid">
            {HOW.map(([Icon, title, text, tone], i) => (
              <motion.div key={title} initial={reduce ? false : { opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: reduce ? 0 : 0.4, delay: reduce ? 0 : (i % 3) * 0.08, ease: "easeOut" }}>
                <div className={`pillar ${tone}`} data-testid={`chi-how-${i}`}>
                  <Icon size={28} style={{ color: tone === "tone-a" ? "#06121F" : "#1EA7E8", marginBottom: 16 }} aria-hidden="true" />
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="sec" data-testid="chi-strutture">
        <div className="wrap" style={{ display: "flex", flexWrap: "wrap", gap: "48px 80px", alignItems: "center" }}>
          <Reveal style={{ flex: "1 1 440px", minWidth: 0 }}>
            <p className="kick">Le nostre strutture</p>
            <h2 className="h2">Di proprietà, pensate per non fermarsi</h2>
            <div className="stats" style={{ marginTop: 36, gridTemplateColumns: "repeat(2, minmax(0,1fr))" }}>
              {STRUTTURE_STATS.map(([n, l], i) => (
                <div className="stat" key={l} data-testid={`struttura-${i}`}>
                  <p className="big">{n}</p>
                  <p>{l}</p>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.08} style={{ flex: "1 1 480px", minWidth: 0 }}>
            <div className="phframe tall">
              <img {...imgProps(img("magazzino-custodia.jpg"), { sizes: "(max-width: 760px) 92vw, 520px" })} alt="Area di custodia merci e valori" />
              <span className="cap">Area di custodia merci e valori</span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="sec-mist sec" data-testid="chi-sedi">
        <div className="wrap">
          <Reveal className="sec-head">
            <p className="kick">Presenza nazionale</p>
            <h2 className="h2">Le nostre sedi</h2>
          </Reveal>
          <div className="sedi">
            {SEDI.map(([k, c, a], i) => (
              <Reveal key={c} delay={i * 0.04}>
                <div className="sede" data-testid={`sede-${i}`}>
                  <p className="k">{k}</p>
                  <h3>{c}</h3>
                  {a && <p>{a}</p>}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="sec" data-testid="chi-licenze">
        <div className="wrap">
          <Reveal className="sec-head">
            <p className="kick">Licenze e certificazioni</p>
            <h2 className="h2">Autorizzati e certificati</h2>
            <p style={{ margin: "22px 0 0", color: "var(--muted)" }}>
              Istituto di vigilanza privata autorizzato ai sensi dell'art. 134 TULPS con licenza prefettizia n. 283167, conforme al D.M. 269/2010. Certificazioni UNI 10891, UNI CEI EN 50518 su due sedi, UNI 10459, ISO 9001, 14001, 45001, 50001, ISO/IEC 27001, SA 8000, UNI/PdR 125:2022, rating EcoVadis, rating di legalità AGCM e codice NATO NCAGE AT086.
            </p>
          </Reveal>
          <div className="certs">
            {CERTS.map(([slug, nm, dsc]) => (
              <div className="cet" key={slug} data-testid={`chi-cert-${slug}`}>
                <img {...imgProps(`/assets/certificazioni/${slug}.png`, { sizes: "240px" })} alt={`Certificazione ${nm}`} />
                <p className="nm">{nm}</p>
                <p>{dsc}</p>
              </div>
            ))}
            <div className="cet" data-testid="chi-cert-rating-legalita">
              <LegalitaBadge />
              <p className="nm">Rating di legalità</p>
              <p>
                Attribuito dall'Autorità Garante della Concorrenza e del Mercato (AGCM) alle imprese che rispettano elevati standard di legalità, trasparenza e responsabilità: due stelle su tre, con il segno più dei requisiti premiali.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="sec" style={{ paddingTop: 0 }} data-testid="chi-clienti">
        <div className="wrap">
          <Reveal className="sec-head center">
            <p className="kick">Clienti</p>
            <h2 className="h2">Ci hanno già scelto</h2>
          </Reveal>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 12 }}>
            {clienti.map(([slug, name]) => (
              <div className="lgt" key={slug} style={{ marginRight: 0 }} data-testid={`chi-client-${slug}`}>
                <div className="lg">
                  <img {...imgProps(`/assets/clienti/${slug}.png`, { sizes: "200px" })} alt={name} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="gruppo" className="sec-blue sec" data-testid="chi-gruppo">
        <div className="wrap">
          <Reveal className="sec-head">
            <p className="kick" style={{ color: "#E2C27C", display: "flex", alignItems: "center", gap: 12 }}>
              <img {...imgProps(aurea("emblema.png"), { sizes: "28px" })} alt="" style={{ height: 28, width: "auto" }} /> Gruppo Aurea Holding 3
            </p>
            <h2 className="h2">Tre competenze, una sicurezza completa</h2>
          </Reveal>
          <div className="gthree">
            <Reveal>
              <div className="gcard" style={{ cursor: "default" }} data-testid="gruppo-orion">
                <img {...imgProps(logo("orion-bianco.png"), { sizes: "180px" })} alt="Orion Security" />
                <h3 style={{ color: "#fff", fontSize: 22, marginBottom: 10 }}>Vigilanza</h3>
                <p>Armata e non armata, obiettivi sensibili, valori, centrale H24.</p>
              </div>
            </Reveal>
            <Reveal delay={0.07}>
              <a href={CONTACT.axentraUrl} className="gcard" target="_blank" rel="noreferrer" data-testid="gruppo-axentra-chi">
                <img {...imgProps(gruppo("axentra-scuro.png"), { sizes: "200px" })} alt="Axentra" />
                <h3 style={{ color: "#fff", fontSize: 22, marginBottom: 10 }}>Tecnologia</h3>
                <p>Videosorveglianza AI, allarmi, controllo accessi, droni e robotica.</p>
                <span className="more">axentrasrl.com</span>
              </a>
            </Reveal>
            <Reveal delay={0.14}>
              <a href={CONTACT.astraUrl} className="gcard" target="_blank" rel="noreferrer" data-testid="gruppo-astra-chi">
                <img {...imgProps(gruppo("astra.png"), { sizes: "200px" })} alt="Astra Investigazioni" className="tall" />
                <h3 style={{ color: "#fff", fontSize: 22, marginBottom: 10 }}>Investigazioni</h3>
                <p>Indagini aziendali, business information e computer forensic.</p>
                <span className="more">inquiria.eu</span>
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="sec" data-testid="chi-compliance">
        <div className="wrap" style={{ display: "flex", flexWrap: "wrap", gap: "40px 90px" }}>
          <Reveal style={{ flex: "1 1 360px" }}>
            <p className="kick">Trasparenza</p>
            <h2 className="h2">Compliance e documenti</h2>
            <p style={{ margin: "24px 0 0", color: "var(--muted)" }}>Etica e responsabilità fanno parte del nostro modo di lavorare.</p>
            <div style={{ marginTop: 30, display: "flex", gap: 12, flexWrap: "wrap" }}>
              <a href={CONTACT.careerUrl} className="btn dark sm" target="_blank" rel="noreferrer" data-testid="chi-lavora-cta">
                Lavora con noi
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.1} style={{ flex: "2 1 560px", minWidth: 0 }}>
            <div className="faqs">
              {DOCS.map((d) => (
                <div className="faq" key={d} style={{ display: "flex", justifyContent: "space-between", gap: 20, alignItems: "center", padding: "20px 0" }} data-testid="chi-doc">
                  <span style={{ fontWeight: 600 }}>{d}</span>
                  <span style={{ fontSize: 13, color: "var(--muted)", whiteSpace: "nowrap" }}>Disponibile su richiesta</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand testid="chi-cta" />
    </>
  );
}
